import { EntityStatusBadgeVariant } from '@frontend/common';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export enum TransactionStateName {
    TRANSACTION_ANY_STATE = 'transaction_any_state',
    TRANSACTION_DRAFT = 'transaction_draft',
    TRANSACTION_CREATED = 'transaction_created',
    TRANSACTION_PROCESSING = 'transaction_processing',
    TRANSACTION_COMPLETED = 'transaction_completed',
    TRANSACTION_COMPLETED_WITH_FAILURES = 'transaction_completed_with_failures',
    TRANSACTION_CANCELLED = 'transaction_cancelled',
    TRANSACTION_FAILED = 'transaction_failed',
    TRANSACTION_DROP_OFF_PENDING = 'transaction_drop_off_pending',
    TRANSACTION_PICK_UP_PENDING = 'transaction_pick_up_pending',
    TRANSACTION_IN_TRANSIT = 'transaction_in_transit',
    TRANSACTION_BACK_TO_SENDER = 'transaction_back_to_sender',
    TRANSACTION_LOST = 'transaction_lost',
    TRANSACTION_SCHEDULED = 'transaction_scheduled'
}

export class TransactionState {
    static readonly TRANSACTION_DRAFT = new TransactionState(
        TransactionStateName.TRANSACTION_DRAFT,
        (
            <FormattedMessage
                id='TransactionState.draft'
                description='The displayed value for the TransactionState draft'
                defaultMessage='Draft'
            />
        )
    );
    static readonly TRANSACTION_CREATED = new TransactionState(
        TransactionStateName.TRANSACTION_CREATED,
        (
            <FormattedMessage
                id='TransactionState.created'
                description='The displayed value for the TransactionState created'
                defaultMessage='Created'
            />
        )
    );
    static readonly TRANSACTION_PROCESSING = new TransactionState(
        TransactionStateName.TRANSACTION_PROCESSING,
        (
            <FormattedMessage
                id='TransactionState.processing'
                description='The displayed value for the TransactionState processing'
                defaultMessage='Processing'
            />
        )
    );
    static readonly TRANSACTION_CANCELLED = new TransactionState(
        TransactionStateName.TRANSACTION_CANCELLED,
        (
            <FormattedMessage
                id='TransactionState.transaction_cancelled'
                description='The displayed value for the TransactionState transaction_cancelled'
                defaultMessage='Cancelled'
            />
        )
    );
    static readonly TRANSACTION_COMPLETED = new TransactionState(
        TransactionStateName.TRANSACTION_COMPLETED,
        (
            <FormattedMessage
                id='TransactionState.transaction_completed'
                description='The displayed value for the TransactionState transaction_completed'
                defaultMessage='Completed'
            />
        )
    );
    static readonly TRANSACTION_COMPLETED_WITH_FAILURES = new TransactionState(
        TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
        (
            <FormattedMessage
                id='TransactionState.transaction_completed_with_failures'
                description='The displayed value for the TransactionState transaction_completed_with_failures'
                defaultMessage='Completed with failures'
            />
        )
    );
    static readonly TRANSACTION_FAILED = new TransactionState(
        TransactionStateName.TRANSACTION_FAILED,
        (
            <FormattedMessage
                id='TransactionState.transaction_failed'
                description='The displayed value for the TransactionState transaction_failed'
                defaultMessage='Failed'
            />
        )
    );
    static readonly TRANSACTION_DROP_OFF_PENDING = new TransactionState(
        TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
        (
            <FormattedMessage
                id='TransactionState.transaction_drop_off_pending'
                description='The displayed value for the TransactionState transaction_drop_off_pending'
                defaultMessage='Awaiting drop-off'
            />
        )
    );
    static readonly TRANSACTION_PICK_UP_PENDING = new TransactionState(
        TransactionStateName.TRANSACTION_PICK_UP_PENDING,
        (
            <FormattedMessage
                id='TransactionState.transaction_pick_up_pending'
                description='The displayed value for the TransactionState transaction_pick_up_pending'
                defaultMessage='Awaiting pick-up'
            />
        )
    );
    static readonly TRANSACTION_ANY_STATE = new TransactionState(
        TransactionStateName.TRANSACTION_ANY_STATE,
        (
            <FormattedMessage
                id='TransactionState.transaction_any_state'
                description='The displayed value for the TransactionState transaction_any_state'
                defaultMessage='Any state'
            />
        )
    );
    static readonly TRANSACTION_IN_TRANSIT = new TransactionState(
        TransactionStateName.TRANSACTION_IN_TRANSIT,
        (
            <FormattedMessage
                id='TransactionState.transaction_in_transit'
                description='The displayed value for the TransactionState transaction_in_transit'
                defaultMessage='In transit'
            />
        )
    );
    static readonly TRANSACTION_BACK_TO_SENDER = new TransactionState(
        TransactionStateName.TRANSACTION_BACK_TO_SENDER,
        (
            <FormattedMessage
                id='TransactionState.transaction_back_to_sender'
                description='The displayed value for the TransactionState transaction_back_to_sender'
                defaultMessage='Back to sender'
            />
        )
    );
    static readonly TRANSACTION_LOST = new TransactionState(
        TransactionStateName.TRANSACTION_LOST,
        (
            <FormattedMessage
                id='TransactionState.transaction_lost'
                description='The displayed value for the TransactionState transaction_lost'
                defaultMessage='Lost'
            />
        )
    );
    static readonly TRANSACTION_SCHEDULED = new TransactionState(
        TransactionStateName.TRANSACTION_SCHEDULED,
        (
            <FormattedMessage
                id='TransactionState.transaction_scheduled'
                description='The displayed value for the TransactionState transaction_scheduled'
                defaultMessage='Scheduled'
            />
        )
    );

    static readonly ALL = [
        TransactionState.TRANSACTION_DRAFT,
        TransactionState.TRANSACTION_CREATED,
        TransactionState.TRANSACTION_CANCELLED,
        TransactionState.TRANSACTION_COMPLETED,
        TransactionState.TRANSACTION_PROCESSING,
        TransactionState.TRANSACTION_COMPLETED_WITH_FAILURES,
        TransactionState.TRANSACTION_FAILED,
        TransactionState.TRANSACTION_DROP_OFF_PENDING,
        TransactionState.TRANSACTION_PICK_UP_PENDING,
        TransactionState.TRANSACTION_IN_TRANSIT,
        TransactionState.TRANSACTION_BACK_TO_SENDER,
        TransactionState.TRANSACTION_LOST,
        TransactionState.TRANSACTION_SCHEDULED,
        TransactionState.TRANSACTION_ANY_STATE
    ];

    static readonly NOT_COMPLETED_CUSTOMER = [
        TransactionState.TRANSACTION_CREATED,
        TransactionState.TRANSACTION_PROCESSING,
        TransactionState.TRANSACTION_DROP_OFF_PENDING,
        TransactionState.TRANSACTION_PICK_UP_PENDING,
        TransactionState.TRANSACTION_IN_TRANSIT,
        TransactionState.TRANSACTION_BACK_TO_SENDER
    ];

    static readonly NOT_COMPLETED = [
        TransactionState.TRANSACTION_CREATED,
        TransactionState.TRANSACTION_PROCESSING,
        TransactionState.TRANSACTION_DROP_OFF_PENDING,
        TransactionState.TRANSACTION_PICK_UP_PENDING
    ];

    static readonly ALL_EXCEPT_DRAFT = TransactionState.ALL.filter(
        (state) => state.value !== TransactionStateName.TRANSACTION_DRAFT && state.value !== TransactionStateName.TRANSACTION_ANY_STATE
    );

    private constructor(
        public readonly value: TransactionStateName,
        public readonly displayName: React.ReactNode
    ) {}

    static getByValue(action: TransactionStateName): TransactionState | undefined {
        return this.ALL.find((a) => a.value == action);
    }

    static getType(state: TransactionStateName): string {
        switch (state) {
            case TransactionStateName.TRANSACTION_DRAFT:
            case TransactionStateName.TRANSACTION_CREATED:
            case TransactionStateName.TRANSACTION_PROCESSING:
                return 'bg-foreground-default';
            case TransactionStateName.TRANSACTION_DROP_OFF_PENDING:
                return 'bg-status-warning';
            case TransactionStateName.TRANSACTION_PICK_UP_PENDING:
                return 'bg-status-info';
            case TransactionStateName.TRANSACTION_COMPLETED:
                return 'bg-status-success';
            case TransactionStateName.TRANSACTION_CANCELLED:
            case TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES:
            case TransactionStateName.TRANSACTION_BACK_TO_SENDER:
            case TransactionStateName.TRANSACTION_CANCELLED:
            case TransactionStateName.TRANSACTION_FAILED:
            case TransactionStateName.TRANSACTION_LOST:
                return 'bg-status-danger';
            case TransactionStateName.TRANSACTION_SCHEDULED:
                return 'bg-status-info';
            default:
                return 'bg-brand-muted';
        }
    }

    static getBadgeVariant(state: TransactionStateName): EntityStatusBadgeVariant {
        switch (state) {
            case TransactionStateName.TRANSACTION_DRAFT:
            case TransactionStateName.TRANSACTION_CREATED:
            case TransactionStateName.TRANSACTION_PROCESSING:
            case TransactionStateName.TRANSACTION_SCHEDULED:
                return EntityStatusBadgeVariant.Draft;
            case TransactionStateName.TRANSACTION_DROP_OFF_PENDING:
                return EntityStatusBadgeVariant.AwaitingDropOff;
            case TransactionStateName.TRANSACTION_PICK_UP_PENDING:
            case TransactionStateName.TRANSACTION_IN_TRANSIT:
                return EntityStatusBadgeVariant.AwaitingPickUp;
            case TransactionStateName.TRANSACTION_COMPLETED:
            case TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES:
                return EntityStatusBadgeVariant.Completed;
            case TransactionStateName.TRANSACTION_CANCELLED:
                return EntityStatusBadgeVariant.Cancelled;
            case TransactionStateName.TRANSACTION_FAILED:
            case TransactionStateName.TRANSACTION_BACK_TO_SENDER:
            case TransactionStateName.TRANSACTION_LOST:
                return EntityStatusBadgeVariant.Failed;
            default:
                return EntityStatusBadgeVariant.Cancelled;
        }
    }
}
